import React, { useState, useEffect } from 'react';
import { Col, Flex, Row, Space } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card } from '@common-web/index.js';
import {
  convertQueryObjToString,
  getFiltersArray,
  getQueryString,
  mapQueryStringToFilterObject,
  showResetButton,
} from './helper';
import { Sidebar } from '../index.js';
import { MdFilterAlt } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQuickLinks, setQuickLinks } from '@reducers/quick-links-slice/index.js';
import { useGetRequest } from '@hooks/index.js';
import FilterChips from './filter-chips';
import RenderFilter from './render-filter';
import AdvancedFilters from './advanced-filters';
import QuickLink from './quick-link';
import SaveQuickLink from './quick-link/save-quick-link';
import FiltersCardSkeleton from './quick-link/filters-card-skeleton';
import { strings } from '@constants/strings';
import { searchFilterClickEvent } from '@ga-events';

const Filters = (props) => {
  const {
    filtersList,
    fetch = () => {},
    rootPath,
    filterChips = [],
    loading = false,
    componentKey = '',
    children = null,
    showManageSearchSkeleton = true,
    shouldPaginate = true,
    pageSize = 20,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [filtersObject, setFiltersObject] = useState({});
  const [selectedVal, setSelectedVal] = useState([]);
  const [pageNumber, setPageNumber] = useState(null);
  const [filtersSidebarOpen, setFiltersSidebarOpen] = useState(false);
  const { quickLinks } = useSelector((state) => state?.quickLinksData);
  const { filters, advancedFilters } = getFiltersArray(filtersList);

  // Load initial data for cascading dropdowns on mount
  useEffect(() => {
    const loadCascadingDropdownData = async () => {
      const queryObject = mapQueryStringToFilterObject(location.search || '');
      const queryObj = queryObject?.queryObj || {};
      
      for (const filter of filtersList) {
        if (filter.type === 'cascading-dropdown') {
          const parentKey = filter.parent?.key;
          const parentValue = queryObj[parentKey];
          
          // If parent value exists in URL and parent has fetch function and setter
          if (parentValue && filter.parent?.fetch && filter.parent?.setter) {
            const hasOptions = filter.parent?.options && filter.parent.options.length > 0;
            
            if (!hasOptions) {
              try {
                const response = await filter.parent.fetch('');
                const fetchKey = filter.parent.fetchKey;
                
                if (response?.[fetchKey]) {
                  const formattedOptions = response[fetchKey].map(item => ({
                    ...item,
                    children: item.children || item.task_outcomes || []
                  }));
                  
                  filter.parent.setter(formattedOptions);
                  
                  // Find and set child options
                  const selectedParent = formattedOptions.find(p => 
                    (filter.parent.setAsSlug ? p.slug : p.id)?.toString() === parentValue?.toString()
                  );
                  
                  if (selectedParent?.children && filter.child?.setter) {
                    filter.child.setter(selectedParent.children);
                  }
                }
              } catch (error) {
                console.error('Error loading cascading dropdown data:', error);
              }
            }
          }
        }
      }
    };
    
    loadCascadingDropdownData();
  }, []); // Run only once on mount

  const fetchData = () => {
    const queryObject = mapQueryStringToFilterObject(location.search || '');
    const searchQuery = getQueryString({
      ...queryObject?.queryObj,
      ...(shouldPaginate ? { limit: pageSize } : {}),
    });
    setFiltersObject({ ...queryObject?.queryObj });
    return searchQuery === '?' ? fetch(shouldPaginate ? `?limit=${pageSize}` : '') : fetch(searchQuery);
  };

  const getQuickLinks = () => {
    dispatch(setQuickLinks([]));
    if (!!componentKey?.length) {
      dispatch(fetchQuickLinks(componentKey));
    }
    return {};
  };

  // useGetRequest({ key: ['quickLinks'], fetch: () => getQuickLinks() });
  useGetRequest({
    key: ['filters', location.search, pageSize],
    fetch: () => fetchData(),
    noCache: true,
  });

  const handleFiltersChange = () => {
    navigate({
      pathname: location.pathname,
      search: convertQueryObjToString({ ...filtersObject, page: 1 }),
    });
    setFiltersSidebarOpen(false);
  };
  return (
    <Flex vertical gap={24}>
      <Sidebar
        sidebarOpen={filtersSidebarOpen}
        setSidebarOpen={setFiltersSidebarOpen}
        heading={'More Filters'}
        description={'Apply filters to organize data accordingly'}
        content={
          <AdvancedFilters
            location={location}
            navigate={navigate}
            setFiltersSidebarOpen={setFiltersSidebarOpen}
            baseList={advancedFilters}
            filtersObject={filtersObject}
            setFiltersObject={setFiltersObject}
            setSelectedVal={setSelectedVal}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            handleFiltersChange={handleFiltersChange}
          />
        }
      />
      <Row style={{ gap: '16px 8px' }} wrap={false}>
        {!!filters?.length &&
          filters?.map((item, index) => {
            const isCascading = item?.type === 'cascading-dropdown';
            return (
              <Col 
                key={index} 
                flex={isCascading ? '1 1 440px' : '1 1 220px'} 
                style={{ maxWidth: isCascading ? 600 : 300 }}
              >
                <RenderFilter
                  item={item}
                  filtersObject={filtersObject}
                  setFiltersObject={setFiltersObject}
                  baseList={filters}
                  setSelectedVal={setSelectedVal}
                  pageNumber={pageNumber}
                  setPageNumber={setPageNumber}
                  handleFiltersChange={handleFiltersChange}
                  loading={loading}
                />
              </Col>
            );
          })}
        {advancedFilters.length > 0 && (
          <Space>
            <Button
              type={'primary-light-secondary'}
              text={'More Filters'}
              icon={'PiSlidersHorizontal'}
              size={'large'}
              // loading={loading}
              onClick={() => {
                setFiltersSidebarOpen((prev) => !prev);
              }}
            />
          </Space>
        )}
        <Space>
          <Button
            text={strings?.search}
            icon={'IoSearchOutline'}
            type={'primary'}
            // loading={loading}
            size={'large'}
            onClick={() => {
              handleFiltersChange()
              searchFilterClickEvent(filtersObject);
            }}
          />
        </Space>
        {children}
      </Row>

      {loading && showManageSearchSkeleton ? (
        <FiltersCardSkeleton filterChips={filterChips} location={location} />
      ) : (
        (quickLinks.length > 0 || filterChips.length > 0) && (
          <Card
            styles={{
              body: {
                '--ant-padding-lg': 'var(--ant-padding)',
              },
            }}
            style={{
              border: '1px solid var(--gray-200)',
            }}
          >
            <Flex vertical gap={12}>
              {filterChips.length > 0 && (
                <Flex align="center" justify="space-between">
                  <Flex align="center" gap={12} className="flexGrow">
                    <Flex align="center" gap={4}>
                      <MdFilterAlt size="1.4em" color="var(--gray-300)" />
                      <span
                        style={{
                          color: 'var(--gray-500)',
                          fontSize: 'var(--ant-font-size-sm)',
                        }}
                      >
                        {'Applied Filters'}:
                      </span>
                    </Flex>
                    <Flex wrap="wrap" gap={8}>
                      {filterChips?.map((chip) => {
                        return (
                          <FilterChips
                            location={location}
                            navigate={navigate}
                            selectedVal={selectedVal}
                            setSelectedVal={setSelectedVal}
                            baseList={filtersList}
                            setPageNumber={setPageNumber}
                            chipFilters={filtersObject}
                            searchKey={chip.searchKey}
                            searchValue={chip.searchValue}
                            chipTitle={chip.title}
                            key={`${chip.searchKey}=${chip.searchValue}`}
                          />
                        );
                      })}

                      {showResetButton(location) && !loading ? (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => {
                            setSelectedVal([]);
                            setPageNumber(1);
                            navigate(rootPath);
                          }}
                          style={{ '--ant-button-padding-inline-sm': '8px' }}
                        >
                          {'Reset Filters'}
                        </Button>
                      ) : null}
                    </Flex>
                  </Flex>
                  {!!componentKey?.length && <SaveQuickLink linkType={componentKey} quickLinks={quickLinks} />}
                </Flex>
              )}
              {quickLinks.length > 0 && <QuickLink />}
            </Flex>
          </Card>
        )
      )}
    </Flex>
  );
};

export default Filters;
